import { useEffect, useRef, useState } from "react";
import { LogIn, Clock3 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAppSelector } from "@/stores/hook";
import { queryClient } from "@/lib/queryClient";
import { useSocketListener } from "@/sockets/hooks/useSocketListener";
import { SocketEvents } from "@/sockets/socketEvents";
import { useIsImpersonating } from "@/features/auth/impersonation/ImpersonationBanner";
import {
  CHECK_IN_STATE_KEY,
  sendCheckInHeartbeat,
  useCheckInMutation,
  useCheckInState,
} from "../api/useCheckIn";
import { HR_KEYS, useHrEnabled, useHrMe } from "@/features/hr/api/hrApi";
import type { SuitesResponse } from "@/features/hr/types/hr.contract";
import { HR_ATTENDANCE_KEYS } from "@/features/hr/attendance/api/attendance.keys";
import { newIdempotencyKey } from "@/features/hr/attendance/api/hrQuery";
import { usePunch, usePunchState } from "@/features/hr/attendance/api/usePunch";
import {
  CHECK_IN_HEARTBEAT_MS,
  clearCheckInPromptDismissal,
  consumeManualCheckOut,
  describeAutoCheckout,
  dismissCheckInPrompt,
  isCheckInPromptDismissed,
} from "./checkInSession";

/**
 * "Check-in / Check-out" — the host mounted once in AppLayout:
 *
 *   1. THE PROMPT. A personal-scope user who is checked out sees "Please
 *      check in first" as soon as the app opens (once per sign-in; "Not now"
 *      is remembered for that sign-in only). It comes back at once when the
 *      server checks them out automatically, saying why.
 *   2. THE HEARTBEAT. While checked in, every two minutes the tab is visible
 *      AND the person has interacted since the last beat, one small POST
 *      keeps their inactivity clock alive (reading without clicking counts).
 *   3. THE PUSH. `checkin:status` (own presence changed elsewhere, or the
 *      org's settings changed) re-reads the state.
 *
 * Renders nothing for everyone the feature doesn't apply to (managers,
 * admins, orgs with it off) and for a "Switch user" session.
 *
 * HR v2 — ONE check-in. With the HR suite on, the prompt's "Check in" is the
 * attendance check-in whenever attendance can be recorded for this person
 * (the server then makes them available for leads in the same call); it stays
 * the lead check-in otherwise (no employee record, or attendance is already
 * running and only lead availability stopped — "Resume"). A settings push
 * (the HR switch flipped somewhere) re-reads the suites, HR and the Manage
 * card too, so every open screen follows the switch within seconds.
 */
export default function CheckInGate() {
  const isAuthed = !!useAppSelector((s) => s.auth.currentUser);
  const impersonating = useIsImpersonating();
  const { data: state } = useCheckInState(isAuthed && !impersonating);
  const checkIn = useCheckInMutation();
  const { enabled: hrOn } = useHrEnabled();
  const hrMe = useHrMe();
  const punchState = usePunchState({
    enabled: hrOn && !impersonating && !!hrMe.data,
    placeholder: hrMe.data?.punch ?? null,
  }).data;
  const hrPunch = usePunch();
  // Attendance can be recorded now → the prompt checks in through attendance.
  const viaAttendance = hrOn && !impersonating && punchState?.mode === "PUNCH" && punchState.canPunchIn;
  // Attendance is already running and only lead availability stopped (idle,
  // a fixed stop time, an admin): the prompt is a "Resume", not a check-in.
  const leadsPaused =
    hrOn &&
    !impersonating &&
    punchState?.mode === "PUNCH" &&
    (punchState.state === "PUNCHED_IN" || punchState.state === "ON_BREAK");
  const [dismissed, setDismissed] = useState<boolean>(() => isCheckInPromptDismissed());
  const [autoNote, setAutoNote] = useState<string | null>(null);

  useSocketListener(SocketEvents.CHECK_IN_STATUS, (data: any) => {
    const note = describeAutoCheckout(data?.reason, data?.minutes);
    if (note) {
      // Checked out by the server — the prompt must come back even if this
      // sign-in had dismissed it.
      setAutoNote(note);
      clearCheckInPromptDismissal();
      setDismissed(false);
    } else if (data?.checkedIn === true) {
      setAutoNote(null);
    }
    void queryClient.invalidateQueries({ queryKey: CHECK_IN_STATE_KEY });
    if (data?.reason === "settings") {
      // The HR switch (which carries Check-in / Check-out) or a check-in
      // option changed: the Manage card, the suite list, then everything HR.
      // The suite list FIRST: if HR was just switched off, refetching the HR
      // reads would only collect 403s — they are dropped instead.
      void queryClient.invalidateQueries({ queryKey: ["lead-form-config"] });
      void (async () => {
        await queryClient.refetchQueries({ queryKey: HR_KEYS.suites });
        const suites = queryClient.getQueryData<SuitesResponse>(HR_KEYS.suites);
        if (suites?.suites?.some((x) => x.key === "hr" && x.isEnabled)) {
          void queryClient.invalidateQueries({ queryKey: HR_KEYS.all });
        } else {
          void queryClient.cancelQueries({ queryKey: HR_KEYS.all });
          queryClient.removeQueries({ queryKey: HR_KEYS.all });
        }
      })();
    } else {
      // Own presence changed elsewhere (another tab, the sweep, an admin):
      // the header's one button shows it.
      void queryClient.invalidateQueries({ queryKey: HR_ATTENDANCE_KEYS.punchState });
    }
  });

  // A checked-in → checked-out flip that the person did NOT ask for (the
  // sweep, an admin) re-opens the prompt even when the socket push didn't
  // reach this tab and the state arrived through a refetch instead.
  const checkedIn = !!state?.checkedIn;
  const prevCheckedInRef = useRef<boolean | null>(null);
  useEffect(() => {
    const prev = prevCheckedInRef.current;
    prevCheckedInRef.current = checkedIn;
    if (prev !== true || checkedIn) return;
    if (consumeManualCheckOut()) {
      // Their own choice — counts as "not now" for this sign-in.
      setDismissed(true);
      return;
    }
    setAutoNote((n) => n ?? "You've been checked out.");
    clearCheckInPromptDismissal();
    setDismissed(false);
  }, [checkedIn]);

  // Heartbeat while checked in.
  const interactedRef = useRef(false);
  useEffect(() => {
    if (!checkedIn || impersonating) return;
    const mark = () => {
      interactedRef.current = true;
    };
    window.addEventListener("pointerdown", mark, { passive: true });
    window.addEventListener("keydown", mark, { passive: true });
    window.addEventListener("scroll", mark, { passive: true, capture: true });
    const tick = async () => {
      if (document.visibilityState !== "visible" || !interactedRef.current) return;
      interactedRef.current = false;
      const next = await sendCheckInHeartbeat();
      if (next) queryClient.setQueryData(CHECK_IN_STATE_KEY, next);
    };
    const id = window.setInterval(() => void tick(), CHECK_IN_HEARTBEAT_MS);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("pointerdown", mark);
      window.removeEventListener("keydown", mark);
      window.removeEventListener("scroll", mark, { capture: true } as any);
    };
  }, [checkedIn, impersonating]);

  if (!state?.applies || impersonating) return null;

  const open = !state.checkedIn && !dismissed;
  const dismiss = () => {
    dismissCheckInPrompt();
    setDismissed(true);
  };
  const busy = checkIn.isPending || hrPunch.isPending;
  const doCheckIn = () => {
    if (!viaAttendance) {
      checkIn.mutate();
      return;
    }
    hrPunch.mutate(
      { type: "IN", idempotencyKey: newIdempotencyKey(), source: "WEB" },
      // Attendance is recorded. If leads could not start yet (outside working
      // hours…) the toast says why — asking again would only answer "already
      // checked in", so the prompt is done for this sign-in.
      { onSuccess: dismiss },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !busy) dismiss();
      }}
    >
      <DialogContent
        className="sm:max-w-[440px] gap-0 p-0 overflow-hidden"
        showCloseButton={false}
        data-testid="check-in-prompt"
      >
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle className="flex items-center gap-2 text-[17px] font-semibold tracking-[-0.3px] text-[var(--color-text-primary)]">
            <span className="flex size-8 items-center justify-center rounded-xl bg-[var(--color-green-1)] text-[var(--color-success)]">
              <LogIn className="size-4" />
            </span>
            {leadsPaused ? "Leads are paused" : "Please check in first"}
          </DialogTitle>
          <DialogDescription className="text-[13px] leading-snug text-[var(--color-text-secondary)]">
            {leadsPaused ? (
              <>
                You&apos;re still checked in for attendance, but new leads and
                automations aren&apos;t being routed to you. Resume to start
                receiving them again.
              </>
            ) : (
              <>
                You&apos;re checked out. While you&apos;re checked out, new
                leads and automations aren&apos;t routed to you. Check in to
                start receiving work
                {viaAttendance ? " — it also records your attendance for today." : "."}
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        {autoNote && (
          <div className="mx-6 mb-2 flex items-start gap-2 rounded-xl border border-[#f1dcae] bg-[#fff6e3] px-3 py-2 text-[12.5px] text-[#6b4700]">
            <Clock3 className="mt-0.5 size-3.5 shrink-0 text-[#b7791f]" />
            <span>{autoNote}</span>
          </div>
        )}
        {state.inactivityMinutes ? (
          <p className="px-6 pb-2 text-[12px] text-[var(--color-text-gray)]">
            You&apos;ll be checked out automatically after{" "}
            {state.inactivityMinutes} minutes of inactivity
            {state.autoCheckoutAt ? ` and at ${state.autoCheckoutAt} every day` : ""}.
          </p>
        ) : state.autoCheckoutAt ? (
          <p className="px-6 pb-2 text-[12px] text-[var(--color-text-gray)]">
            You&apos;ll be checked out automatically at {state.autoCheckoutAt} every day.
          </p>
        ) : null}
        <div className="flex items-center justify-end gap-2 border-t border-[#e6ebe8] bg-[#fafbfa] px-6 py-4">
          <Button
            type="button"
            variant="ghost"
            className="h-9 rounded-xl px-3 text-[13px] text-[var(--color-text-secondary)]"
            disabled={busy}
            onClick={dismiss}
          >
            Not now
          </Button>
          <Button
            type="button"
            variant="primary"
            className="h-9 rounded-xl px-4 text-[13px]"
            disabled={busy}
            onClick={doCheckIn}
          >
            {leadsPaused ? (busy ? "Resuming…" : "Resume leads") : busy ? "Checking in…" : "Check in"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
